import { useEffect, useState } from 'react'
import type { PresentationStatus } from '../store/registrationsSlice'

/**
 * IndexedDB-backed store for presentation file blobs.
 *
 * Blobs are persisted in the browser's IndexedDB under the database
 * "proaxiv-presentations", object store "files". This allows the same file
 * to be read from any tab on the same origin (including the organizer
 * management page) and to survive page reloads.
 *
 * All public functions are async and degrade gracefully (no-op / false) when
 * IndexedDB is unavailable (e.g. private browsing with restrictions, or the
 * jsdom test environment before fake-indexeddb is loaded).
 */

const DB_NAME = 'proaxiv-presentations'
const DB_VERSION = 1
const STORE_NAME = 'files'

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE_NAME)
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

/** Store a presentation file blob for a given registration id. */
export async function setPresentationFile(registrationId: string, file: File): Promise<void> {
  try {
    const db = await openDB()
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      tx.objectStore(STORE_NAME).put(file, registrationId)
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    })
  } catch {
    // IndexedDB unavailable — silently no-op
  }
}

/** Returns true when a downloadable blob exists in IndexedDB for the given registration. */
export async function hasPresentationFile(registrationId: string): Promise<boolean> {
  try {
    const db = await openDB()
    return await new Promise<boolean>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly')
      const req = tx.objectStore(STORE_NAME).getKey(registrationId)
      req.onsuccess = () => resolve(req.result !== undefined)
      req.onerror = () => reject(req.error)
    })
  } catch {
    return false
  }
}

/**
 * Trigger a browser download for the stored file.
 * Reads the blob from IndexedDB, creates a temporary object URL, clicks a
 * hidden anchor, then revokes the URL.
 * Does nothing if no file is stored for the given id.
 */
export async function downloadPresentationFile(registrationId: string): Promise<void> {
  try {
    const db = await openDB()
    const file = await new Promise<File | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly')
      const req = tx.objectStore(STORE_NAME).get(registrationId)
      req.onsuccess = () => resolve(req.result as File | undefined)
      req.onerror = () => reject(req.error)
    })
    if (!file) return

    const url = URL.createObjectURL(file)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = file.name
    anchor.style.display = 'none'
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(url)
  } catch {
    // IndexedDB unavailable — silently no-op
  }
}

/** Remove a stored presentation blob (e.g. when the registration is deleted). */
export async function deletePresentationFile(registrationId: string): Promise<void> {
  try {
    const db = await openDB()
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      tx.objectStore(STORE_NAME).delete(registrationId)
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    })
  } catch {
    // IndexedDB unavailable — silently no-op
  }
}

/**
 * React hook that asynchronously checks whether a presentation file is
 * available in IndexedDB for the given registration.
 *
 * Returns true only when:
 *   - the status is 'uploaded' or 'accepted', AND
 *   - IndexedDB contains a blob for the given registrationId.
 *
 * Re-checks whenever registrationId or status changes.
 */
export function usePresentationFileAvailable(
  registrationId: string,
  status: PresentationStatus
): boolean {
  const [available, setAvailable] = useState(false)

  useEffect(() => {
    if (status !== 'uploaded' && status !== 'accepted') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAvailable(false)
      return
    }
    let cancelled = false
    hasPresentationFile(registrationId).then((has) => {
      if (!cancelled) setAvailable(has)
    })
    return () => {
      cancelled = true
    }
  }, [registrationId, status])

  return available
}
